import axios from 'axios';
import { createAsyncThunk } from '@reduxjs/toolkit';
import { Recruitment, RecruitmentApplication } from 'redux/schemas/models/mentoring-program-recruitments';


type MentoringProgramRecruitmentParams = {
  programId: number;
  recruitmentId?: number;
  recruitment?: Recruitment;
};

// This is "Enabling Mentor Recruitment"
export const createMentoringProgramRecruitment = createAsyncThunk<
Recruitment,
MentoringProgramRecruitmentParams
>(
  'CREATE_MENTORING_PROGRAM_RECRUITMENT',
  async ({ programId }) => {
    const response = await axios.post(
      `/mentorship_programs/${programId}/mentorship_program_recruitments`,
      {
        mentorship_program_recruitment: {
          role: 'mentor',
        },
      },
    );

    return response.data.result;
  },
);

export const updateMentoringProgramRecruitment = createAsyncThunk<
Recruitment,
MentoringProgramRecruitmentParams
>(
  'UPDATE_MENTORING_PROGRAM_RECRUITMENT',
  async ({ programId, recruitment }) => {
    const response = await axios.put(
      `/mentorship_programs/${programId}/mentorship_program_recruitments/${recruitment?.id}`,
      {
        mentorship_program_recruitment: {
          ...recruitment,
        },
      },
    );

    return response.data.result;
  },
);

// This is "Disabling Mentor Recruitment"
export const deleteMentoringProgramRecruitment = createAsyncThunk<
boolean,
MentoringProgramRecruitmentParams
>(
  'DELETE_MENTORING_PROGRAM_RECRUITMENT',
  async ({ programId, recruitment }) => {
    const response = await axios.delete(
      `/mentorship_programs/${programId}/mentorship_program_recruitments/${recruitment?.id}`,
    );

    return response.data.result;
  },
);

export const getMentoringProgramRecruitment = createAsyncThunk<
Recruitment,
MentoringProgramRecruitmentParams
>(
  'GET_MENTORING_PROGRAM_RECRUITMENT',
  async ({ programId, recruitmentId }) => {
    const response = await axios.get(
      `/mentorship_programs/${programId}/mentorship_program_recruitments/${recruitmentId}`,
    );

    return response.data.result;
  },
);

/**
 * Recruitment Application APIs
 */

// This is "Express Interest"
export const createMentoringProgramRecruitmentApplication = createAsyncThunk<
RecruitmentApplication,
MentoringProgramRecruitmentParams
>(
  'CREATE_MENTORING_PROGRAM_RECRUITMENT_APPLICATION',
  async ({ programId, recruitmentId }) => {
    const response = await axios.post(
      `/mentorship_programs/${programId}/mentorship_program_recruitments/${recruitmentId}/mentorship_program_recruitment_applications`,
    );

    return response.data.result;
  },
);

export const getCurrentMentoringProgramRecruitmentApplication = createAsyncThunk<
RecruitmentApplication,
MentoringProgramRecruitmentParams
>(
  'GET_CURRENT_MENTORING_PROGRAM_RECRUITMENT_APPLICATION',
  async ({ programId, recruitmentId }) => {
    const response = await axios.get(
      `/mentorship_programs/${programId}/mentorship_program_recruitments/${recruitmentId}/mentorship_program_recruitment_applications/current`,
    );

    return response.data.result;
  },
);

// This is "Withdraw Interest"
export const deleteMentoringProgramRecruitmentApplication = createAsyncThunk<
boolean,
MentoringProgramRecruitmentParams
>(
  'DELETE_MENTORING_PROGRAM_RECRUITMENT_APPLICATION',
  async ({ programId, recruitmentId }) => {
    const response = await axios.delete(
      `/mentorship_programs/${programId}/mentorship_program_recruitments/${recruitmentId}/mentorship_program_recruitment_applications/current`,
    );

    return response.data.result;
  },
);

const sortByToParamPrefix = {
  name: 'name_',
  dateSubmitted: 'date_submitted_',
};
type GetMentoringProgramRecruitmentApplicationsParams = MentoringProgramRecruitmentParams & {
  page?: number;
  pageSize?: number;
  textSearch?: string;
  sortBy?: 'name' | 'dateSubmitted';
  sortDesc?: boolean;
};

export const getMentoringProgramRecruitmentApplications = createAsyncThunk<
RecruitmentApplication[],
GetMentoringProgramRecruitmentApplicationsParams
>(
  'GET_MENTORING_PROGRAM_RECRUITMENT_APPLICATIONS',
  async ({ programId, recruitmentId, page, pageSize, textSearch, sortBy, sortDesc }) => {
    let sortingParam;
    if (sortBy) {
      const suffix = sortDesc ? 'desc' : 'asc';
      sortingParam = `${sortByToParamPrefix[sortBy]}${suffix}`;
    }

    const params = {
      page,
      page_size: pageSize,
      text_search: textSearch || undefined,
    };

    if (sortingParam) {
      params[sortingParam] = true;
    }

    const response = await axios.get(
      `/mentorship_programs/${programId}/mentorship_program_recruitments/${recruitmentId}/mentorship_program_recruitment_applications`,
      {
        params,
      },
    );

    return response.data.result;
  },
);
